/* ──────────────────────────────────────────────────────────────────────
   <KineticLesson /> — Reusable mini-training-lesson component.
   ──────────────────────────────────────────────────────────────────────
   Mike's directive Feb 2026:
   - Background = Mike on video OR Mike in a still photo (with Ken Burns slow zoom)
   - Foreground = kinetic typography that prints across the screen
   - Optional banner image at the top (logo, course title, brand strap)
   - Each lesson = 2-3 minutes, ~15-30 card moments
   - Use this for every Faitharama mini course

   Props:
     videoUrl   — MP4 background  (if provided, takes priority)
     audioUrl   — audio track     (used when no video)
     posterUrl  — still photo of Mike (background when audioUrl mode, also poster for video)
     bannerUrl  — optional logo/strap pinned at the top
     title, subtitle, duration, cards
   ────────────────────────────────────────────────────────────────────── */
import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, RotateCcw } from "lucide-react";

const G   = "#2E9F3A";
const O   = "#F39A1F";
const RED = "#C9202E";
const INK = "#0F172A";

export default function KineticLesson({
  videoUrl,
  audioUrl,
  posterUrl,
  bannerUrl,
  title    = "Faitharama Mini Lesson",
  subtitle = "",
  cards    = [],
  duration = 180,
}) {
  const mediaRef       = useRef(null);    // works for both <video> and <audio>
  const [playing, setPlaying] = useState(false);
  const [muted,   setMuted]   = useState(true);
  const [seconds, setSeconds] = useState(0);

  const useAudio = !videoUrl && audioUrl;

  /* Drive the kinetic timeline off real media currentTime so audio
     and headlines stay locked together. Fall back to a manual timer
     for the very first render in case onTimeUpdate is delayed. */
  useEffect(() => {
    const m = mediaRef.current;
    if (!m) return;
    function onTime() { setSeconds(m.currentTime); }
    function onEnd()  { setPlaying(false); }
    m.addEventListener("timeupdate", onTime);
    m.addEventListener("ended", onEnd);
    return () => {
      m.removeEventListener("timeupdate", onTime);
      m.removeEventListener("ended", onEnd);
    };
  }, []);

  function togglePlay() {
    const m = mediaRef.current;
    if (!m) return;
    if (playing) { m.pause(); setPlaying(false); }
    else {
      // Audio mode: unmute on first click since user gesture allows it.
      if (useAudio) { m.muted = false; setMuted(false); }
      m.play().catch(()=>{}); setPlaying(true);
    }
  }
  function toggleMute() {
    const m = mediaRef.current;
    if (!m) return;
    m.muted = !m.muted;
    setMuted(m.muted);
  }
  function restart() {
    const m = mediaRef.current;
    if (m) { m.currentTime = 0; m.play().catch(()=>{}); }
    setSeconds(0);
    setPlaying(true);
  }

  /* Active card = latest one whose time <= current seconds. */
  const sorted = [...cards].sort((a, b) => a.time - b.time);
  let activeIdx = -1;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].time <= seconds) activeIdx = i; else break;
  }
  const active = activeIdx >= 0 ? sorted[activeIdx] : null;
  const pct = Math.min(100, (seconds / duration) * 100);

  return (
    <div
      data-testid="kinetic-lesson"
      className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-black"
      style={{ aspectRatio: "16 / 9" }}
    >
      {/* ── Background: video OR photo (Ken Burns) ── */}
      {videoUrl ? (
        <video
          ref={mediaRef}
          src={videoUrl}
          poster={posterUrl}
          muted={muted}
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(1.05) brightness(0.62)" }}
        />
      ) : (
        <>
          <img
            src={posterUrl}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover kenburns-bg"
            style={{ filter: "saturate(1.05) brightness(0.62)" }}
            draggable={false}
          />
          <audio ref={mediaRef} src={audioUrl} preload="metadata" />
        </>
      )}

      {/* Vignette overlay so text always reads */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(15,23,42,0.18) 0%, rgba(15,23,42,0.55) 65%, rgba(15,23,42,0.85) 100%)",
        }}
      />

      {/* ── Optional top banner ── */}
      {bannerUrl && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1 bg-black/35 backdrop-blur rounded-full">
          <img
            src={bannerUrl}
            alt="Banner"
            className="block h-7 md:h-9 w-auto"
            draggable={false}
          />
        </div>
      )}

      {/* Top strap (label + timer) */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 md:px-8 py-3 z-10">
        <div className="flex items-center gap-2 text-white text-[10px] md:text-xs tracking-[0.32em] uppercase font-bold">
          <span style={{ color: O }}>★</span>
          <span>FAITHARAMA · MINI LESSON</span>
        </div>
        <div className="text-white text-[10px] md:text-xs tracking-[0.22em] uppercase font-bold opacity-75">
          {formatTime(seconds)} / {formatTime(duration)}
        </div>
      </div>

      {/* Kinetic foreground card */}
      <div
        key={activeIdx}
        className="absolute inset-0 flex flex-col items-center justify-center px-6 md:px-12 text-center pointer-events-none"
      >
        {active ? (
          <CardText card={active} />
        ) : (
          <CardText card={{ text: title, kind: "title", subtitle }} />
        )}
      </div>

      {/* Bottom: progress + controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="w-full h-1 bg-white/15">
          <div
            className="h-full transition-[width] duration-200 ease-linear"
            style={{ width: `${pct}%`, background: G }}
          />
        </div>

        <div className="flex items-center justify-between px-5 md:px-7 py-3 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
          <div className="flex items-center gap-2">
            <button
              onClick={togglePlay}
              data-testid="kinetic-play-btn"
              aria-label={playing ? "Pause" : "Play"}
              className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-lg hover:scale-105 transition"
              style={{ background: G }}
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <button
              onClick={toggleMute}
              data-testid="kinetic-mute-btn"
              aria-label={muted ? "Unmute" : "Mute"}
              className="w-10 h-10 rounded-full flex items-center justify-center text-white bg-white/10 hover:bg-white/25 transition"
            >
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <button
              onClick={restart}
              data-testid="kinetic-restart-btn"
              aria-label="Restart"
              className="w-10 h-10 rounded-full flex items-center justify-center text-white bg-white/10 hover:bg-white/25 transition"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          <div className="text-white text-[11px] md:text-sm tracking-[0.16em] uppercase font-bold text-right">
            <div style={{ color: O }}>{title}</div>
            {subtitle && <div className="opacity-70 text-[10px] md:text-xs">{subtitle}</div>}
          </div>
        </div>
      </div>

      {/* Ken Burns slow zoom — keeps the still photo feeling alive */}
      <style>{`
        @keyframes kenburns {
          0%   { transform: scale(1.05) translate(0,0); }
          50%  { transform: scale(1.15) translate(-1.5%, 1%); }
          100% { transform: scale(1.05) translate(0,0); }
        }
        .kenburns-bg { animation: kenburns 28s ease-in-out infinite; transform-origin: center 35%; }
      `}</style>
    </div>
  );
}

function CardText({ card }) {
  const { text, kind, subtitle } = card;
  const sizeClass = {
    title:   "text-3xl md:text-6xl",
    tagline: "text-2xl md:text-5xl",
    quote:   "text-xl md:text-3xl italic",
    stat:    "text-4xl md:text-7xl",
  }[kind] || "text-3xl md:text-5xl";
  const accent = { title: G, tagline: O, quote: "#fff", stat: RED }[kind] || G;

  return (
    <div className="kinetic-card-enter text-center max-w-5xl">
      <h2
        className={`font-serif-display uppercase leading-[0.95] text-white ${sizeClass}`}
        style={{ textShadow: "0 2px 16px rgba(0,0,0,0.55)" }}
      >
        {text}
      </h2>
      {subtitle && (
        <p className="text-base md:text-xl text-white mt-4 opacity-90"
           style={{ textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>
          {subtitle}
        </p>
      )}
      <div className="mx-auto mt-5 h-1 rounded-full" style={{ width: 96, background: accent }} />

      <style>{`
        @keyframes kineticIn {
          0%   { opacity: 0; transform: translateY(18px) scale(0.98); letter-spacing: -0.02em; filter: blur(8px); }
          40%  { opacity: 1; transform: translateY(0)    scale(1);    letter-spacing: 0;       filter: blur(0);  }
          100% { opacity: 1; transform: translateY(0)    scale(1); }
        }
        .kinetic-card-enter { animation: kineticIn 0.85s cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>
    </div>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}
